import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled]):not([type=hidden])",
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(",");

let lockCount = 0;
let previousOverflow = "";

function lockScroll() {
  if (lockCount++ === 0) {
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
}

function unlockScroll() {
  if (--lockCount === 0) {
    document.body.style.overflow = previousOverflow;
  }
}

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** Extra content on the title row, e.g. a delete button. */
  titleAccessory?: React.ReactNode;
  footer?: React.ReactNode;
  size?: "sm" | "md" | "lg";
  /** Set to false for irreversible flows such as "delete account". */
  dismissable?: boolean;
  children: React.ReactNode;
}

const WIDTHS = {
  sm: "sm:max-w-[400px]",
  md: "sm:max-w-[520px]",
  lg: "sm:max-w-[680px]",
};

/**
 * Accessible modal: portal, focus trap, Escape to close, scroll lock and
 * focus restoration. On phones it docks to the bottom like a sheet.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  titleAccessory,
  footer,
  size = "md",
  dismissable = true,
  children,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    const panel = panelRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    lockScroll();

    // Let the panel mount before moving focus. Prefer the first text field so
    // the user can start typing immediately; headings and the close button
    // are the fallback for confirmation dialogs.
    const timer = window.setTimeout(() => {
      const field = panel?.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])',
      );
      const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
      (field ?? first ?? panel)?.focus();
    }, 20);

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && dismissable) {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !panel) return;

      const nodes = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((node) => node.offsetParent !== null);
      if (nodes.length === 0) return;

      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;

      if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("keydown", onKeyDown, true);
      unlockScroll();
      previouslyFocused?.focus?.();
    };
  }, [open, dismissable]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      role="presentation"
    >
      <div
        className="absolute inset-0 animate-fade-in bg-black/35 backdrop-blur-[2px] dark:bg-black/60"
        onClick={dismissable ? onClose : undefined}
        aria-hidden
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        aria-describedby={description ? "dialog-description" : undefined}
        tabIndex={-1}
        className={cn(
          "relative flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-line bg-paper shadow-pop sm:rounded-xl",
          WIDTHS[size],
          "animate-slide-up sm:animate-scale-in",
        )}
      >
        <header className="flex items-start gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2
              id="dialog-title"
              className="truncate text-[15px] font-semibold tracking-tight"
            >
              {title}
            </h2>
            {description && (
              <p
                id="dialog-description"
                className="mt-0.5 text-[12px] leading-5 text-ink-faint"
              >
                {description}
              </p>
            )}
          </div>
          {titleAccessory}
          <button
            type="button"
            onClick={onClose}
            className="-mr-1 rounded-md p-1.5 text-ink-faint transition hover:bg-shell hover:text-ink"
            aria-label="Đóng"
          >
            <X size={16} />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {children}
        </div>

        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-line bg-shell/60 px-5 py-3 pb-safe">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
